import { AddIcon, ChevronDownIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Divider,
    Flex,
    Icon,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Select,
    Spinner,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tooltip,
    Tr,
    useToast,
    Checkbox,
    useColorModeValue,
    useDisclosure,
} from '@chakra-ui/react';
import BuildUrlParamCarousel from 'components/adspresso/BuildUrlParamCarousel';
import BuildUrlParamsUpdate from 'components/adspresso/BuildUrlParamsUpdate';
import FaceBookPageSelector from 'components/adspresso/FaceBookPageSelector';
import InstagramAccountUpdateSelector from 'components/adspresso/InstagramAccountUpdateSelector';
import UrlParametersUpdate from 'components/adspresso/UrlParametersUpdate';
import DriveModal from 'components/drive/DriveModal';
import { useFormik } from 'formik';
import { useState } from 'react';
import { MdDelete, MdOutlineEdit } from 'react-icons/md';
import PhoneInput from 'react-phone-input-2';
import { useGetAdCreativeQuery, useGetInstantFormsByPageIdQuery, useUpdateAdMutation } from 'store/campaigns/adsApi';
import * as Yup from 'yup';
import { getCallToActionOptions, removeEmptyValues, truncateString } from 'utils/functions';
import {
    call_to_action_options,
    call_to_action_options_instantForms,
    call_to_action_options_app_promptions,
    call_to_action_options_whatsapp,
    call_to_action_options_for_app_leads_and_sales,
    call_to_action_options_Awareness_Traffic_Engagement
} from 'utils/callToActions';
import { useGetAdvertisableAppsQuery } from 'store/campaigns/adsetFormApi';
import AdPreviewCaro from 'components/genericForms/AdPreviewCaro';
import MessageTemplateModal from 'components/genericForms/MessageTemplateModal';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Ad name is required'),
    cpa: Yup.number()
        .typeError('must be a number')
        .positive('must be a positive number'),
    creative: Yup.object().shape({
        name: Yup.string().required('Creative name is required'),
        object_story_spec: Yup.object().shape({
            link_data: Yup.object().shape({
                destination_type: Yup.string(),
                link: Yup.string()
                    .when('destination_type', {
                        is: (value) => ['WEBSITE', 'OUTCOME_AWARENESS'].includes(value),
                        then: () => Yup.string()
                            .required('URL is required'),
                        otherwise: () => Yup.string().notRequired(),
                    }),
                // call_to_action: Yup.object().shape({
                //     type: Yup.string().required('Call to action type is required'),
                //     value: Yup.object().shape({
                //         destinationType: Yup.string(),
                //         link: Yup.string()
                //             .when('destinationType', {
                //                 is: (value) => value === 'PHONE_CALL',
                //                 then: () => Yup.string()
                //                     .transform((value) => value.slice(2)) // Remove the first two digits (e.g., country code like "91")
                //                     .matches(/^[0-9]{10,15}$/, 'Phone number must be at least 10 digits') // Ensure remaining digits are at least 10 digits
                //                     .required('Phone number is required'), // Ensure phone number is provided
                //                 otherwise: () => Yup.string().notRequired(),
                //             }),
                //       }),
                // }),
                child_attachments: Yup.array().of(
                    Yup.object().shape({
                        link: Yup.string().required('URL is required'),
                        picture: Yup.string().required('Creative is required'),
                        call_to_action: Yup.object().shape({
                            value: Yup.object().shape({
                                link: Yup.string().when('type', {
                                    is: 'CALL_NOW',
                                    then: Yup.string().matches(/tel:\+?[0-9]{10,15}/, 'Phone number must be at least 10 digits')
                                }),
                            }),
                        }),
                        //description: Yup.string().required('Description is required'),
                    })
                ).min(1, 'At least one creative is required'),
            }),
            page_id: Yup.string().required('Facebook Page is required'),
        }),
    }),
});

const CarouselAdUpdateForm = ({ adData, adsAccountData, onClose, onSuccess }) => {
    const toast = useToast();
    const background = useColorModeValue('gray.50', 'gray.800');
    const buttonBackgroundColor = useColorModeValue('gray.50', 'gray.800');
    const backgroundColor = useColorModeValue('#F5F3F9', '#1e1530');
    const { isOpen: isMessageModalOpen, onOpen: onMessageModalOpen, onClose: onMessageModalClose } = useDisclosure();
    const [isDriveModalOpen, setIsDriveModalOpen] = useState(false);
    const [isUrlModalOpen, setUrlModalOpen] = useState(false);
    const [isSingleUrlModalOpen, setSingleUrlModalOpen] = useState(false);
    const [isUrlParamModalOpen, setUrlParamModalOpen] = useState(false);
    const [selectedCreativeIndex, setSelectedCreativeIndex] = useState(null);
    const [selectedFileIndex, setSelectedFileIndex] = useState(null);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [isEnabled, setIsEnabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isMediaTypeSelectionOpen, setIsMediaTypeSelectionOpen] = useState(false);
    const { data: adCreative, isLoading } = useGetAdCreativeQuery(
        {
            creativeId: adData?.id,
            adsAccountId: adsAccountData?.account_id
        },
        { skip: !adData?.id }
    );

    const { data: advertisableApps } = useGetAdvertisableAppsQuery({adsAccountId: adsAccountData?.account_id}, {skip: !adsAccountData?.account_id});

    const [updateAd] = useUpdateAdMutation();

    const pageId = adData?.adset?.promoted_object?.page_id;

    const formattedPageIds = Array.isArray(pageId) ? pageId : [pageId];

    const { data: instantForms } = useGetInstantFormsByPageIdQuery(
        { pageIds: formattedPageIds },
        { skip: !formattedPageIds.length || adCreative?.adset?.destination_type !== 'ON_AD' }
    );

    let pageIdToSend = null;
    if (
        adCreative?.adset?.destination_type === "PHONE_CALL" &&
        adData?.adset?.promoted_object?.page_id
    ) {
        pageIdToSend = adData.adset.promoted_object.page_id;
    }else if (
        adCreative?.campaign?.objective === "OUTCOME_AWARENESS"
    ){
        pageIdToSend = adData.adset.promoted_object.page_id;
    }

    const formik = useFormik({
        initialValues: {
            name: adData?.name || "",
            hash: adData?.hash || '',
            cpa: adData?.cpa || '',
            page_backed_insta_id: '',
            enroll_status: adCreative?.creative?.contextual_multi_ads?.enroll_status ? adCreative?.creative?.contextual_multi_ads?.enroll_status : 'OPT_OUT',
            creative: {
                name: adCreative?.creative?.name || "",
                url_tags: adCreative?.creative?.url_tags || '',
                app_product_page_id: adCreative?.creative?.asset_feed_spec?.app_product_page_id,
                app_id: adCreative?.creative?.template_url_spec?.config?.app_id || '',
                object_story_spec: {
                    link_data: {
                        message: adCreative?.creative?.object_story_spec?.link_data?.message || "",
                        link: adCreative?.creative?.object_story_spec?.link_data?.link || "",
                        caption: adCreative?.creative?.object_story_spec?.link_data?.caption || '',
                        destination_type:  adCreative?.campaign?.objective === "OUTCOME_AWARENESS" ? adCreative?.campaign?.objective : adCreative?.adset?.destination_type ? adCreative?.adset?.destination_type : '',
                        call_to_action: {
                            type: adCreative?.creative?.object_story_spec?.link_data?.call_to_action?.type,
                            value: {
                                link: adCreative?.adset?.destination_type === 'ON_AD' ? adCreative?.creative?.object_story_spec?.link_data?.call_to_action?.value?.lead_gen_form_id : adCreative?.adset?.destination_type === 'APP' ? adCreative?.creative?.object_story_spec?.link_data?.call_to_action?.value?.app_link : adCreative?.creative?.object_story_spec?.link_data?.call_to_action?.value?.link,
                                destinationType: adCreative?.adset?.destination_type ? adCreative?.adset?.destination_type : '',
                            }
                        },
                        child_attachments: adCreative?.creative?.object_story_spec?.link_data?.child_attachments || [
                            {
                                link: "", name: "", description: "", image_hash: "", video_id: "", picture: "", hash: "", caption: "",
                                call_to_action: {
                                    type: "",
                                    value: {
                                        link: ""
                                    }
                                }
                            },
                        ],
                        page_welcome_message: (() => {
                            const raw = adCreative?.creative?.object_story_spec?.link_data?.page_welcome_message;
                              return typeof raw === 'string' ? JSON.parse(raw) : raw;
                        })() 
                    },
                    page_id: adCreative?.creative?.object_story_spec?.page_id || "",
                    instagram_user_id: adCreative?.creative?.object_story_spec?.instagram_user_id || '',
                },
            },
        },
        validationSchema,
        enableReinitialize: true,
        validateOnMount: true,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            setIsSaving(true);

            const formatPhoneNumber = (phoneNumber) => {
                if (!phoneNumber.startsWith('tel:+')) {
                    return `tel:+${phoneNumber}`;
                }
                return phoneNumber;
            };

            // Function to check if creative has changed
            const isCreativeChanged = () => {
                const original = adCreative?.creative || {};
                const updated = values.creative || {};

                return (
                    original.name !== updated.name ||
                    original.url_tags !== updated.url_tags ||
                    original.object_story_spec?.link_data?.message !== updated.object_story_spec?.link_data?.message ||
                    original.object_story_spec?.link_data?.link !== updated.object_story_spec?.link_data?.link ||
                    original.object_story_spec?.link_data?.call_to_action?.type !== updated.object_story_spec?.link_data?.call_to_action?.type ||
                    original.object_story_spec?.link_data?.call_to_action?.value?.link !== updated.object_story_spec?.link_data?.call_to_action?.value?.link ||
                    original.object_story_spec?.link_data?.child_attachments !== updated.object_story_spec?.link_data?.child_attachments ||
                    original.object_story_spec?.page_id !== updated.object_story_spec?.page_id ||
                    original.object_story_spec?.instagram_user_id !== updated.object_story_spec?.instagram_user_id ||
                    original.object_story_spec?.link_data?.caption !== updated.object_story_spec?.link_data?.caption ||
                    original?.template_url_spec?.config?.app_id !== updated.app_id
                );
            };

            const hasCreativeChanged = isCreativeChanged();

            const initialPayload = {
                name: values.name,
                hash: values.hash,
                ...(typeof values.cpa === 'string' && values.cpa.trim() && { cpa: values.cpa }),
                ...(hasCreativeChanged && {
                    creative: {
                        name: values.creative.name,
                        object_type : "SHARE",
                        ...(values.creative?.url_tags?.trim() && { url_tags: values.creative.url_tags }),
                        object_story_spec: {
                            link_data: {
                                ...(values.creative.object_story_spec.link_data.message?.trim() && {
                                    message: values.creative.object_story_spec.link_data.message
                                }),
                                link: adCreative?.adset?.destination_type === 'ON_AD'
                                    ? "http://fb.me/"
                                    : adCreative?.adset?.destination_type === 'WHATSAPP'
                                    ? "https://api.whatsapp.com/send"
                                    : adCreative?.adset?.destination_type === 'APP'
                                        ? adData?.adset?.promoted_object?.object_store_url
                                        : values.creative.object_story_spec.link_data.link
                                            ? values.creative.object_story_spec.link_data.link
                                            : `https://facebook.com/${values.creative.object_story_spec.page_id}/`,
                                ...(values.creative.object_story_spec.link_data.call_to_action.type !== 'CALL_NOW' && { 
                                    caption: values.creative.object_story_spec.link_data.caption,
                                }),
                                call_to_action: adCreative?.adset?.destination_type === 'ON_AD' ? {
                                    type: values.creative.object_story_spec.link_data.call_to_action.type,
                                    value: {
                                        lead_gen_form_id: values.creative.object_story_spec.link_data.call_to_action?.value?.link
                                    }
                                    }
                                    : adCreative?.adset?.destination_type === 'APP' ? {
                                        type: values.creative.object_story_spec.link_data.call_to_action.type,
                                        value: {
                                            app_link: values.creative.object_story_spec.link_data?.link
                                        }
                                    } : adCreative?.adset?.destination_type === 'WHATSAPP' ? {
                                        type: values.creative.object_story_spec.link_data.call_to_action.type,
                                        value: {
                                            app_destination: "WHATSAPP"
                                        }
                                    } : (values.creative.object_story_spec.link_data.call_to_action.type === 'CALL_NOW'
                                            ? {
                                                ...(["OUTCOME_ENGAGEMENT", "OUTCOME_TRAFFIC"].includes(adCreative?.campaign?.objective)) ?{
                                                    type: values.creative.object_story_spec.link_data.call_to_action.type,
                                                    value: {
                                                        link: values.creative.object_story_spec.link_data.call_to_action?.value?.link
                                                    }
                                                }:{
                                                    type: values.creative.object_story_spec.link_data.call_to_action.type,
                                                    value: {
                                                        link: formatPhoneNumber(values.creative.object_story_spec.link_data.child_attachments[values.creative.object_story_spec.link_data.child_attachments.length - 1]?.call_to_action.value.link)
                                                    }
                                                }
                                            } : { type: values.creative.object_story_spec.link_data.call_to_action.type} ),
                                child_attachments: values.creative.object_story_spec.link_data.child_attachments
                                    .map(child => {
                                        const { image_hash, id, call_to_action, ...rest } = child;

                                        const cleanedRest = Object.fromEntries(
                                            Object.entries(rest).filter(([key, value]) => value && value !== "")
                                        );

                                        if ( adCreative?.adset?.destination_type === 'PHONE_CALL') {
                                            return {
                                                ...cleanedRest,
                                                call_to_action: {
                                                    type: values.creative.object_story_spec.link_data.call_to_action?.type,
                                                    value: {
                                                        link: call_to_action?.value?.link
                                                            ? formatPhoneNumber(call_to_action.value.link)
                                                            : undefined
                                                    }
                                                }
                                            };
                                        }

                                        if ( adCreative?.adset?.destination_type === 'APP') {
                                            return {
                                                ...cleanedRest,
                                                link: adData?.adset?.promoted_object?.object_store_url,
                                                call_to_action: {
                                                    type: values.creative.object_story_spec.link_data.call_to_action?.type,
                                                    value: {
                                                        app_link: call_to_action?.value?.app_link
                                                    }
                                                }
                                            };
                                        }

                                        if ( adCreative?.adset?.destination_type === 'ON_AD') {
                                            return {
                                                ...cleanedRest,
                                                call_to_action: {
                                                    type: values.creative.object_story_spec.link_data.call_to_action?.type,
                                                },
                                                link: "http://fb.me/"
                                            };
                                        }

                                        return {
                                            ...cleanedRest,
                                            call_to_action: {
                                                type: values.creative.object_story_spec.link_data.call_to_action?.type,
                                            }
                                        };
                                    }),
                                ...((['WHATSAPP'].includes(adCreative?.adset?.destination_type)) && { page_welcome_message: values?.creative.object_story_spec.link_data.page_welcome_message }),
                            },
                            page_id: values.creative.object_story_spec.page_id,
                            ...(values.creative.object_story_spec.instagram_user_id?.trim() && {
                                instagram_user_id: values.creative.object_story_spec.instagram_user_id,
                            }),
                        },
                        contextual_multi_ads: {
                            enroll_status: values.enroll_status,
                        },
                        ...(adCreative?.adset?.destination_type === 'APP' && {
                            asset_feed_spec: {
                                app_product_page_id: values.creative.app_product_page_id,
                            }
                        }),
                        ...(adCreative?.campaign?.objective === "OUTCOME_AWARENESS" && {
                            template_url_spec: {
                                config: {
                                    app_id: values.creative.app_id,
                                }
                            }
                        }),
                    },
                }),
                objective: adCreative?.campaign?.objective,
                destination_type: adCreative?.adset?.destination_type,
                adsAccount: {
                    id: adsAccountData?.id,
                    adsAccountId: adsAccountData?.account_id,
                    adsAccountName: adsAccountData?.name,
                    businessName: adsAccountData?.business?.name,
                    businessId: adsAccountData?.business?.id
                },
            };

            const payload = removeEmptyValues(initialPayload);

            updateAd({ creativeId: adData.id, payload })
                .unwrap()
                .then(response => {
                    onSuccess();
                    toast({
                        position: "top-right",
                        title: "Ad updated",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                })
                .catch(error => {
                    toast({
                        position: "top-right",
                        title: "Error updating ad",
                        description: error?.data?.message,
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                })
                .finally(() => {
                    setSubmitting(false);
                    resetForm();
                    setIsSaving(false);
                    setIsEnabled(false);
                    onClose();
                });
        },
    });

    const handleOpenDriveModal = (mediaType, index) => {
        setSelectedMedia(mediaType);
        setSelectedCreativeIndex(index);
        setIsDriveModalOpen(true);
    };

    const handleMediaSelect = (file) => {
        if (file && file.length > 0) {
            const selectedFile = file[0];

            // Update the specific creative in the Formik state
            formik.setFieldValue('creative.object_story_spec.link_data.child_attachments', formik.values.creative.object_story_spec.link_data.child_attachments.map((creative, index) => {
                if (index === selectedCreativeIndex) {
                    return {
                        ...creative,
                        ...(selectedMedia === 'video' && {
                            video_id: selectedFile?.creativeId,
                            hash: selectedFile?.hash,
                            picture: selectedFile?.picture,
                        }),
                        ...(selectedMedia === 'image' && {
                            image_hash: selectedFile?.creativeId,
                            hash: selectedFile?.hash,
                            picture: selectedFile?.url,
                        }),
                    };
                }
                return creative;
            }));
        }
        setIsDriveModalOpen(false);
        setSelectedCreativeIndex(null);
    };

    const handleAddCreativeClick = () => {
        setIsMediaTypeSelectionOpen(!isMediaTypeSelectionOpen);
    };

    const handleMediaTypeSelection = (type) => {
        setSelectedMedia(type);
        setIsMediaTypeSelectionOpen(false);

        const newAttachment = {
            link: "",
            name: "",
            description: "",
            picture: "",
            id: "",
            call_to_action: {
                type: 'SHOP_NOW',
                value: {
                    link: ''
                }
            }
        };

        const pageId = formik.values.creative.object_story_spec.page_id;

        if (formik.values.creative.object_story_spec.link_data.call_to_action.type === 'CALL_NOW') {
            newAttachment.call_to_action = {
                type: 'CALL_NOW',
                value: {
                    link: '',
                }
            };
            newAttachment.link = pageId ? `https://facebook.com/${pageId}/` : '';
        }

        if (type === 'image') {
            newAttachment.image_hash = type;
            newAttachment.id = type;
        } else if (type === 'video') {
            newAttachment.video_id = type;
            newAttachment.id = type;
        }

        formik.setFieldValue('creative.object_story_spec.link_data.child_attachments', [
            ...(formik.values.creative.object_story_spec.link_data.child_attachments || []),
            newAttachment
        ]);
    };


    if (isLoading) {
        return <Flex justify="center" align="center" height="60vh">
            <Spinner size="md" />
        </Flex>
    }

    const handleUrlChange = (constructedUrl) => {
        formik.setFieldValue('creative.object_story_spec.link_data.link', constructedUrl);
    };

    const handleSingleUrlChange = (constructedUrl, fileIndex) => {

        // Make a copy of the current child_attachments array
        const updatedChildAttachments = [...formik.values.creative.object_story_spec.link_data.child_attachments];

        // Update the 'link' of the specific fileIndex
        updatedChildAttachments[fileIndex] = {
            ...updatedChildAttachments[fileIndex],
            link: constructedUrl,
        };

        // Now use Formik's setFieldValue to update the form field
        formik.setFieldValue(
            "creative.object_story_spec.link_data.child_attachments",
            updatedChildAttachments
        );
    };

    const handleUrlParamChange = (constructedUrl) => {
        formik.setFieldValue('creative.url_tags', constructedUrl);
    };

    const filteredInstantForms = instantForms?.length > 0 && instantForms
        .map((formResponse) => formResponse?.data?.data) // Get the 'data' array from each formResponse
        .flat() // Flatten the array to get all forms in one array
        .filter((form) => form?.page_id === pageId);

    return (
        <form onSubmit={formik.handleSubmit}>
            <Flex gap={4}>
                <Flex direction={'column'} maxH={'1050px'} overflowY={'auto'} flex={0.4}>
                    <Flex direction={'column'} gap={'20px'} borderRadius="md" bg={background} p={4} mb={4}>
                        {/* Ad Name Section */}
                        <Flex direction={'column'} gap={'10px'}>
                            <Text fontWeight="bold">Ad name</Text>
                            <Input
                                name="name"
                                placeholder="Enter your ad name here..."
                                bg="#fff"
                                size='sm'
                                borderRadius="md"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.name}</Text>
                            )}
                        </Flex>

                        <Flex direction="column" gap={'10px'}>
                            <Text fontWeight="bold">Facebook Page</Text>
                            <FaceBookPageSelector
                                adsAccount={adsAccountData}
                                selectedPageId={formik.values.creative.object_story_spec.page_id}
                                pageIdToSend={pageIdToSend}
                                onSelectedPage={(pageId) => {
                                    formik.setFieldValue('creative.object_story_spec.page_id', pageId);

                                    // Reset the Instagram actor ID to an empty string
                                    formik.setFieldValue('creative.object_story_spec.instagram_user_id', '');
                                }}
                            />
                            {formik.touched.creative?.object_story_spec?.page_id && formik.errors.creative?.object_story_spec?.page_id && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.page_id}</Text>
                            )}
                        </Flex>

                        <Flex direction="column" gap={1}>
                            <Text fontWeight="bold">Instagram accounts</Text>
                            <InstagramAccountUpdateSelector
                                pageID={formik.values.creative?.object_story_spec?.page_id}
                                adsAccount={adsAccountData}
                                selectedInstPageId={formik.values.creative?.object_story_spec?.instagram_user_id}
                                onSelectedPage={(pageId) => {
                                    formik.setFieldValue('creative.object_story_spec.instagram_user_id', pageId);
                                }}
                                onPageBack={(pageId) => {
                                    formik.setFieldValue('page_backed_insta_id', pageId);
                                }}
                            />
                            {formik.errors.creative?.object_story_spec?.instagram_user_id && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.instagram_user_id}</Text>
                            )}
                        </Flex>

                        <Divider />

                        {/* Multi-advertiser ads Section */}
                        <Flex direction="column" borderRadius="md" gap={2}>
                            <Checkbox
                                onChange={(e) => {
                                    formik.setFieldValue("enroll_status", e.target.checked ? 'OPT_IN' : 'OPT_OUT');
                                }}
                                colorScheme="brand"
                                isChecked={formik.values.enroll_status === 'OPT_IN'}
                            >
                                <Flex align={'center'}>
                                    <Text fontWeight="bold" fontSize={'14px'}>Multi-advertiser ads &nbsp;</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p="5px">
                                                <Text>Your ads can appear alongside other ads in the same ad unit to help people discover products and services from businesses that are personalized to them. Your ad creative may be resized or cropped to fit the ad unit.</Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                            </Checkbox>
                        </Flex>
                    </Flex>

                    <Flex direction={'column'} gap={'15px'} borderRadius="md" bg={background} p={4} mb={4} style={{ overflow: 'visible' }}>
                        {/* Creative Attachments Section */}
                        {formik.values.creative.object_story_spec.link_data.child_attachments.map((creative, index) => {

                            return (
                                <Flex direction={'column'} key={index} borderWidth={1} p={4} borderRadius="md" gap={'20px'}>
                                    <Flex direction={'column'} gap={'10px'}>
                                        <Flex justifyContent="space-between" alignItems="center" >
                                            <Text fontWeight="bold">Headline</Text>
                                            <Tooltip label="Delete" fontSize="xs">
                                                <Box>
                                                    <Icon
                                                        color={'red.500'}
                                                        _hover={{ color: 'gray.400' }}
                                                        as={MdDelete}
                                                        cursor="pointer"
                                                        boxSize={5}
                                                        onClick={() => {
                                                            const updatedAttachments = formik.values.creative.object_story_spec.link_data.child_attachments.filter((_, i) => i !== index);
                                                            formik.setFieldValue('creative.object_story_spec.link_data.child_attachments', updatedAttachments);
                                                        }}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        </Flex>
                                        <Input
                                            name={`creative.object_story_spec.link_data.child_attachments.${index}.name`}
                                            placeholder="Enter a name"
                                            size='sm'
                                            borderRadius="md"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={creative.name}
                                        />
                                        {formik.touched.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.name && formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.name && (
                                            <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.name}</Text>
                                        )}
                                    </Flex>

                                    <Flex direction="column" gap={'20px'}>
                                        {/* Description Section */}
                                        { adCreative?.adset?.destination_type === 'APP' ? null :
                                            <Flex direction="column" gap={'10px'} >
                                                <Text fontWeight="bold">Description</Text>
                                                <Input
                                                    name={`creative.object_story_spec.link_data.child_attachments.${index}.description`}
                                                    placeholder="Enter a description"
                                                    size='sm'
                                                    borderRadius="md"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={creative.description}
                                                />
                                                {formik.touched.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.description && formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.description && (
                                                    <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.description}</Text>
                                                )}
                                            </Flex>
                                        }

                                        { adCreative?.adset?.destination_type === 'PHONE_CALL' ?
                                            (
                                                <>
                                                    <Flex direction="column" gap={1}>
                                                        <Text fontWeight="bold">Call to Action</Text>

                                                        <Select
                                                            name={`creative.object_story_spec.link_data.child_attachments.${index}.call_to_action.type`}
                                                            bg="#fff"
                                                            size="sm"
                                                            borderRadius="md"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            value={creative?.call_to_action?.type}
                                                            isDisabled
                                                        >
                                                            <option value="CALL_NOW">Call Now</option>
                                                        </Select>
                                                    </Flex>

                                                    <Flex direction="column" gap={1}>
                                                        <Text fontWeight="bold">Phone Number</Text>
                                                        <PhoneInput
                                                            name={`creative.object_story_spec.link_data.child_attachments.${index}.call_to_action.value.link`}
                                                            value={creative?.call_to_action?.value?.link}
                                                            onChange={(value) => formik.setFieldValue(`creative.object_story_spec.link_data.child_attachments.${index}.call_to_action.value.link`, value)}
                                                            country={'in'}
                                                        />
                                                    </Flex>

                                                    {formik.touched.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.call_to_action?.value?.link && formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.call_to_action?.value?.link && (
                                                        <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.call_to_action?.value?.link}</Text>
                                                    )}
                                                </>
                                            ) : adCreative?.adset?.destination_type === 'WEBSITE' || adCreative?.campaign?.objective === "OUTCOME_AWARENESS" ? (
                                                <>
                                                    <Flex direction="column" gap={'10px'}>
                                                        <Text fontWeight="bold">Website URL</Text>
                                                        <Input
                                                            name={`creative.object_story_spec.link_data.child_attachments.${index}.link`}
                                                            placeholder="Enter a URL"
                                                            size='sm'
                                                            borderRadius="md"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            value={creative?.link}
                                                        />
                                                        <Flex mt={2}>
                                                            <Button onClick={() => {
                                                                setSingleUrlModalOpen(true)
                                                                setSelectedFileIndex(index)
                                                            }}
                                                                variant="link"
                                                                color='brand'>
                                                                <Text fontSize={'12px'} >{'Build a URL parameter'}</Text>
                                                            </Button>
                                                        </Flex>
                                                        {formik.touched.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.link && formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.link && (
                                                            <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.link}</Text>
                                                        )}
                                                    </Flex>
                                                </>
                                            ) : adCreative?.adset?.destination_type === 'APP' && adCreative?.campaign?.objective === "OUTCOME_APP_PROMOTION" ? (
                                                <Flex direction="column" gap={1}>
                                                    <Flex alignItems="center" gap={2}>
                                                        <Text fontWeight="bold" fontSize={'14px'}>Deferred deep link</Text>
                                                        <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="sm" p="5px">
                                                                    <Text fontWeight={'bold'}>Using a deferred deep link</Text><br></br>
                                                                    <Text mb={2}>A deferred deep link is a URL that sends people to your app in the respective app store. Once people install your app, the link will take them to a specific screen, such as a product page.</Text>
                                                                    <Text mb={2}>To use a deferred deep link, you need to first connect the Facebook SDK. You can use any of these link types:</Text>
                                                                    <Box>
                                                                        <Text>* Android app link</Text>
                                                                        <Text>* Custom URL scheme</Text>
                                                                        <Text>* Facebook app link</Text>
                                                                    </Box>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" />
                                                        </Tooltip>
                                                    </Flex>
                                                    <Input
                                                        name={`creative.object_story_spec.link_data.child_attachments.${index}.call_to_action.value.app_link`}
                                                        placeholder="Enter a deferred deep link URL"
                                                        size='sm'
                                                        borderRadius="md"
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        value={creative?.call_to_action?.value?.app_link}
                                                    />
                                                </Flex>
                                            ) :adCreative?.adset?.destination_type === 'APP' && ["OUTCOME_SALES", "OUTCOME_LEADS"].includes(adCreative?.campaign?.objective) ? (
                                                <Flex direction="column" gap={1}>
                                                    <Flex alignItems="center" gap={2}>
                                                        <Text fontWeight="bold" fontSize={'14px'}>Deep link</Text>
                                                        <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="sm" p="5px">
                                                                    <Text>Send people to a specific place in your app. You can use an Apple universal link, Android App Link, custom URL scheme or Facebook App Link.</Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" />
                                                        </Tooltip>
                                                    </Flex>
                                                    <Input
                                                        name={`creative.object_story_spec.link_data.child_attachments.${index}.call_to_action.value.app_link`}
                                                        placeholder="Enter a deferred deep link URL"
                                                        size='sm'
                                                        borderRadius="md"
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        value={creative?.call_to_action?.value?.app_link}
                                                    />
                                                </Flex>
                                        ) : null}

                                        {adCreative?.adset?.destination_type === 'WHATSAPP' &&  ['OUTCOME_SALES', 'OUTCOME_LEADS'].includes(adData?.campaign?.objective) &&  (creative?.video_id) ? (
                                            // Display Link Section
                                            <Box direction="column" mb={4}>
                                            <Flex alignItems="center" mb="5px" gap={2}>
                                                <Text fontWeight="bold" fontSize={'14px'}>Display link</Text>
                                                <Tooltip
                                                borderRadius={5}
                                                label={
                                                    <Box fontSize="sm" p="5px">
                                                    <Text>
                                                        Show a shortened link instead of your full website URL. The link should go to the same domain as your website URL. The display link won't show in all placements.
                                                    </Text>
                                                    </Box>
                                                }
                                                fontSize="md"
                                                >
                                                <InfoOutlineIcon pointerEvents="all" />
                                                </Tooltip>
                                            </Flex>
                                            <Input
                                                name={`creative.object_story_spec.link_data.child_attachments.${index}.caption`}
                                                placeholder="Enter the link you want to show on your ad"
                                                size="sm"
                                                borderRadius="md"
                                                value={creative.caption}
                                                onChange={formik.handleChange}
                                            />
                                            </Box>
                                        ): null}
                                    </Flex>


                                    <Flex direction={'column'} gap={'10px'}>
                                        <Text fontWeight="bold">Creative</Text>
                                        <Flex direction="row" gap={'10px'} alignItems={'center'}>
                                            {/* Conditionally render either an image or the add icon */}
                                            {((creative?.id === 'image' && !creative?.picture) || (creative?.id === 'video' && !creative?.picture)) ? (
                                                <AddIcon />
                                            ) : (
                                                <img
                                                    src={creative?.video_id ? creative?.picture : creative?.picture || ''}
                                                    alt={creative?.name}
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover', border: '0.5px solid grey', borderRadius: '5px' }}
                                                />
                                            )}

                                            <Button
                                                bg={buttonBackgroundColor}
                                                size="xs"
                                                borderWidth={1}
                                                onClick={() => handleOpenDriveModal(creative?.video_id ? 'video' : 'image', index)}
                                            >
                                                {creative?.id === 'image' && !creative?.picture ?
                                                    "Add Image" :
                                                    creative?.id === 'video' && !creative?.picture ?
                                                        "Add Video" :
                                                        (creative?.video_id ? "Change Video" : "Change Image")
                                                }
                                            </Button>
                                        </Flex>
                                        {formik.touched.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.picture && formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.picture && (
                                            <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.child_attachments?.[index]?.picture}</Text>
                                        )}
                                    </Flex>

                                </Flex>
                            )
                        })}

                        <Flex justifyContent="flex-end" >
                            <Menu>
                                <MenuButton
                                    as={Button}
                                    onClick={handleAddCreativeClick}
                                    colorScheme='brand'
                                    _expanded={{ bg: 'brand.600' }}
                                    rightIcon={<ChevronDownIcon />}
                                    size='sm'
                                    sx={{ minWidth: "200px" }}
                                >
                                    {'Add Creative'}
                                </MenuButton>
                                <MenuList sx={{
                                    minWidth: "160px",
                                    position: "absolute",
                                    left: 10
                                }}>
                                    <MenuItem
                                        onClick={() => handleMediaTypeSelection('image')}
                                        fontSize="12px"
                                    >
                                        Add image creative
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => handleMediaTypeSelection('video')}
                                        fontSize="12px"
                                    >
                                        Add video creative
                                    </MenuItem>
                                </MenuList>

                            </Menu>
                        </Flex>

                    </Flex>
                    <Flex direction={'column'} gap={2} borderRadius="md" bg={background} p={4} mb={4}>
                        <Box>
                            <Flex direction={'column'} gap={'20px'}>
                                {/* Primary Text Section */}
                                <Flex direction="column" gap={'10px'}>
                                    <Text fontWeight="bold">Primary Text</Text>
                                    <Textarea
                                        name="creative.object_story_spec.link_data.message"
                                        placeholder="Enter a text"
                                        bg="#fff"
                                        size='sm'
                                        borderRadius="md"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.creative.object_story_spec.link_data.message}
                                    />
                                    {formik.touched.creative?.object_story_spec?.link_data?.message && formik.errors.creative?.object_story_spec?.link_data?.message && (
                                        <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.message}</Text>
                                    )}
                                </Flex>

                                {adCreative?.adset?.destination_type !== 'PHONE_CALL' ? (
                                    <Flex direction="column" gap={'10px'}>
                                        <Text fontWeight="bold">Call to Action</Text>

                                        {adCreative?.adset?.destination_type === 'ON_AD' ? (
                                            <Select
                                                name="creative.object_story_spec.link_data.call_to_action.type"
                                                bg="#fff"
                                                size="sm"
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.type}
                                            >
                                                {getCallToActionOptions(
                                                    call_to_action_options_instantForms,
                                                    formik.values.creative.object_story_spec.link_data.call_to_action.type
                                                ).map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : adCreative?.adset?.destination_type === 'APP' && adData?.campaign?.objective === 'OUTCOME_APP_PROMOTION' ? (
                                            <Select
                                                name="creative.object_story_spec.link_data.call_to_action.type"
                                                bg="#fff"
                                                size="sm"
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.type}
                                            >
                                                {getCallToActionOptions(
                                                    call_to_action_options_app_promptions,
                                                    formik.values.creative.object_story_spec.link_data.call_to_action.type
                                                ).map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : adCreative?.adset?.destination_type === 'APP' && ["OUTCOME_SALES", "OUTCOME_LEADS"].includes(adCreative?.campaign?.objective) ? (
                                            <Select
                                                name="creative.object_story_spec.link_data.call_to_action.type"
                                                bg="#fff"
                                                size="sm"
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.type}
                                            >
                                                {getCallToActionOptions(
                                                    call_to_action_options_for_app_leads_and_sales,
                                                    formik.values.creative.object_story_spec.link_data.call_to_action.type
                                                ).map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : adCreative?.adset?.destination_type === 'WHATSAPP' ? (
                                            <Select
                                                name="creative.object_story_spec.link_data.call_to_action.type"
                                                bg="#fff"
                                                size="sm"
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.type}
                                                isDisabled={adCreative?.campaign?.objective === 'OUTCOME_LEADS'}
                                            >
                                                {getCallToActionOptions(
                                                    call_to_action_options_whatsapp,
                                                    formik.values.creative.object_story_spec.link_data.call_to_action.type
                                                ).map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : adCreative?.campaign?.objective === "OUTCOME_AWARENESS" || (["OUTCOME_TRAFFIC", "OUTCOME_ENGAGEMENT"].includes(adCreative?.campaign?.objective) && adCreative?.adset?.destination_type === 'WEBSITE') ? (
                                            <Select
                                                name="creative.object_story_spec.link_data.call_to_action.type"
                                                bg="#fff"
                                                size="sm"
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.type}
                                            >
                                                {getCallToActionOptions(
                                                    call_to_action_options_Awareness_Traffic_Engagement,
                                                    formik.values.creative.object_story_spec.link_data.call_to_action.type
                                                ).map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Select
                                                name="creative.object_story_spec.link_data.call_to_action.type"
                                                bg="#fff"
                                                size="sm"
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.type}
                                            >
                                                {getCallToActionOptions(
                                                    call_to_action_options,
                                                    formik.values.creative.object_story_spec.link_data.call_to_action.type
                                                ).map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}

                                        {formik.touched.creative?.object_story_spec?.link_data?.call_to_action?.type &&
                                            formik.errors.creative?.object_story_spec?.link_data?.call_to_action?.type && (
                                                <Text fontSize="12px" color="red.500">
                                                    {formik.errors.creative.object_story_spec.link_data.call_to_action.type}
                                                </Text>
                                            )}
                                    </Flex>
                                ) : null}

                                {adCreative?.adset?.destination_type === 'PHONE_CALL' && ["OUTCOME_TRAFFIC", "OUTCOME_ENGAGEMENT"].includes(adCreative?.campaign?.objective) ? (
                                    <Flex direction="column" gap={'10px'}>
                                        <Text fontWeight="bold">Phone Number</Text>
                                        <PhoneInput
                                            name="creative.object_story_spec.link_data.call_to_action.value.link"
                                            value={formik.values.creative.object_story_spec.link_data.call_to_action?.value?.link}
                                            onChange={(value) => formik.setFieldValue('creative.object_story_spec.link_data.call_to_action.value.link', value)}
                                            country={'in'}
                                        />
                                        {formik.errors.creative?.object_story_spec?.link_data?.call_to_action?.value?.link && (
                                            <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.call_to_action?.value?.link}</Text>
                                        )}
                                    </Flex>
                                ) : adCreative?.adset?.destination_type === 'WEBSITE' || adCreative?.campaign?.objective === "OUTCOME_AWARENESS" ? (
                                    <Flex direction="column" gap={'10px'}>
                                        <Text fontWeight="bold">Website URL</Text>
                                        <Input
                                            name="creative.object_story_spec.link_data.link"
                                            placeholder="Enter a webiste url"
                                            bg="#fff"
                                            size='sm'
                                            borderRadius="md"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.creative.object_story_spec.link_data.link}
                                        />
                                        {formik.errors.creative?.object_story_spec?.link_data?.link && (
                                            <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.link_data?.link}</Text>
                                        )}
                                    </Flex>
                                ) : adCreative?.adset?.destination_type === 'ON_AD' && adData?.campaign?.objective === 'OUTCOME_LEADS' ? (
                                    <>
                                        <Flex alignItems="center" gap={2} mt={2}>
                                            <Text fontWeight="bold">Instant form</Text>
                                            <Tooltip
                                                borderRadius={5}
                                                label={
                                                    <Box fontSize="sm" p="5px">
                                                        <Text>Make connections with people by letting them send contact information and other details to you through a form.</Text>
                                                    </Box>
                                                }
                                                fontSize="md"
                                            >
                                                <InfoOutlineIcon pointerEvents="all" />
                                            </Tooltip>
                                        </Flex>

                                        {/* Table Layout */}
                                        {filteredInstantForms?.length > 0 ? (
                                            <RadioGroup onChange={(value) => formik.setFieldValue('creative.object_story_spec.link_data.call_to_action.value.link', value)} value={formik.values.creative.object_story_spec.link_data.call_to_action.value.link} colorScheme="brand">
                                                <Table variant="simple">
                                                    <Thead>
                                                        <Tr>
                                                            <Th textTransform={'none'} color={'none'} fontSize="14px">Form title</Th>
                                                            <Th textTransform={'none'} color={'none'} fontSize="14px">Creation date</Th>
                                                        </Tr>
                                                    </Thead>
                                                   <Tbody>
                                                        {filteredInstantForms?.map((form) => (
                                                            <Tr key={form?.id}>
                                                                <Td>
                                                                    <Flex alignItems="center" gap={2}>
                                                                        <Radio value={form?.id} />
                                                                        <Text color={form.status === 'DRAFT' ? "gray.500" : "none"} fontSize={'12px'}>
                                                                            {truncateString(form?.name, 25)}
                                                                        </Text>
                                                                    </Flex>
                                                                </Td>
                                                                <Td>
                                                                    <Text fontSize={'12px'}>
                                                                        {new Date(form?.created_time).getFullYear()}-
                                                                        {String(new Date(form?.created_time).getMonth() + 1).padStart(2, '0')}-
                                                                        {String(new Date(form?.created_time).getDate()).padStart(2, '0')}
                                                                    </Text>
                                                                </Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </RadioGroup>
                                        ) : (
                                            <Text fontSize="14px" color="gray.500">No form data available</Text>
                                        )}
                                    </>
                                ) : adCreative?.adset?.destination_type === 'APP' && adCreative?.campaign?.objective === "OUTCOME_APP_PROMOTION"  ? (
                                    <>
                                        <Flex direction="column" gap={1}>
                                            <Flex alignItems="center" gap={2}>
                                                <Text fontWeight="bold">Deferred deep link</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="sm" p="5px">
                                                            <Text fontWeight={'bold'}>Using a deferred deep link</Text><br></br>
                                                            <Text mb={2}>A deferred deep link is a URL that sends people to your app in the respective app store. Once people install your app, the link will take them to a specific screen, such as a product page.</Text>
                                                            <Text mb={2}>To use a deferred deep link, you need to first connect the Facebook SDK. You can use any of these link types:</Text>
                                                            <Box>
                                                                <Text>* Android app link</Text>
                                                                <Text>* Custom URL scheme</Text>
                                                                <Text>* Facebook app link</Text>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" />
                                                </Tooltip>
                                            </Flex>
                                            <Input
                                                name="creative.object_story_spec.link_data.call_to_action.value.link"
                                                placeholder="Enter a deferred deep link URL"
                                                bg="#fff"
                                                size='sm'
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.value.link}
                                            />
                                        </Flex>
                                        <Flex direction="column" gap={1}>
                                            <Flex alignItems="center" gap={2}>
                                                <Text fontWeight="bold">Custom store listing</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="sm" p="5px">
                                                            <Text fontWeight={'bold'}>Using a custom store listing</Text><br></br>
                                                            <Text mb={2}>A custom store listing ID sends people who click your ad to a landing page that you've customised in the Google Play Store.</Text>
                                                            <Text mb={2}>Use this feature to create multiple custom product pages with different media and text that speak to your various audiences.</Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" />
                                                </Tooltip>
                                            </Flex>
                                            <Input
                                                name="creative.app_product_page_id"
                                                placeholder="Enter custome store listing ID"
                                                bg="#fff"
                                                size='sm'
                                                borderRadius="md"
                                                value={formik.values.creative.app_product_page_id}
                                                onChange={formik.handleChange}
                                            />
                                        </Flex>
                                    </>
                                ) : adCreative?.adset?.destination_type === 'APP' && ["OUTCOME_SALES", "OUTCOME_LEADS"].includes(adCreative?.campaign?.objective)  ? (
                                    <>
                                        <Flex direction="column" gap={1}>
                                            <Flex alignItems="center" gap={2}>
                                                <Text fontWeight="bold">Deep link</Text>
                                                <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="sm" p="5px">
                                                            <Text>Send people to a specific place in your app. You can use an Apple universal link, Android App Link, custom URL scheme or Facebook App Link.</Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" />
                                                </Tooltip>
                                            </Flex>
                                            <Input
                                                name="creative.object_story_spec.link_data.call_to_action.value.link"
                                                placeholder="Enter the deep link URL"
                                                bg="#fff"
                                                size='sm'
                                                borderRadius="md"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.creative.object_story_spec.link_data.call_to_action.value.link}
                                            />
                                        </Flex>
                                    </>
                                ) : null}

                                {['WHATSAPP'].includes(adCreative?.adset?.destination_type) ? (
                                    <>
                                        <Flex>
                                            <Text fontWeight={'bold'} fontSize={'sm'}>Message template</Text>
                                        </Flex>
                                    
                                        {(() => {
                                        const welcomeData = formik?.values?.creative?.object_story_spec?.link_data?.page_welcome_message;
                                            
                                        const type = welcomeData?.text_format?.customer_action_type;

                                        const greeting = welcomeData?.text_format?.message?.text || '';
                                        const autofillMessage = welcomeData?.text_format?.message?.autofill_message?.content || '';
                                        const iceBreakers = welcomeData?.text_format?.message?.ice_breakers || [];

                                        return (
                                            <Box mb={2}>
                                                <Box border={'1px solid'} borderColor={'gray.200'} p={2} borderRadius={'md'} bg={backgroundColor}>
                                                    <Text fontSize={'14px'}><strong>Greeting:</strong> {greeting}</Text>

                                                    {type === 'autofill_message' && (
                                                        <Text fontSize={'14px'}><strong>Pre-filled message:</strong> {autofillMessage}</Text>
                                                    )}

                                                    {type === 'ice_breakers' && (
                                                        <>
                                                        <Text fontSize={'14px'}><strong>Frequently asked questions</strong></Text>
                                                        {iceBreakers.map((q, i) => (
                                                            <Text key={i} fontSize="14px">
                                                            {i + 1}. {q.title}
                                                            </Text>
                                                        ))}
                                                        </>
                                                    )}
                                                </Box>

                                                <Button
                                                    variant="outline"
                                                    backgroundColor="transparent" 
                                                    size={'sm'}
                                                    leftIcon={<MdOutlineEdit />}
                                                    mt={2}
                                                    onClick={() => {
                                                        onMessageModalOpen();
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </Box>
                                        );
                                        })()}
                                    </>
                                ) : null}

                                {adCreative?.campaign?.objective === "OUTCOME_AWARENESS" || (["OUTCOME_TRAFFIC","OUTCOME_ENGAGEMENT"].includes(adCreative?.campaign?.objective) && adCreative?.adset?.destination_type === 'WEBSITE')  ? 
                                    <Flex direction="column" gap={1}>
                                        <Text fontWeight="bold" >Display link</Text>
                                        <Input
                                            name="creative.object_story_spec.link_data.caption"
                                            placeholder="Enter the deep link URL"
                                            bg="#fff"
                                            size='sm'
                                            borderRadius="md"
                                            value={formik.values.creative.object_story_spec.link_data.caption}
                                            onChange={formik.handleChange}
                                        />
                                    </Flex>
                                : null}

                                {adCreative?.campaign?.objective === "OUTCOME_AWARENESS" ? 
                                    <Flex direction={'column'} gap={1}>
                                        <Flex alignItems="center" gap={2}>
                                            <Text fontWeight="bold" >Mobile app</Text>
                                            <Tooltip
                                                borderRadius={5}
                                                label={
                                                    <Box fontSize="sm" p="5px">
                                                        <Text>Only select a mobile app if you are using a universal link (iOS or Android app link).</Text>
                                                    </Box>
                                                }
                                                fontSize="md"
                                            >
                                                <InfoOutlineIcon pointerEvents="all" />
                                            </Tooltip>
                                        </Flex>
                                        <Select
                                            name={`creative.app_id`}
                                            bg="#fff"
                                            size="sm"
                                            borderRadius="md"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik?.values?.creative?.app_id}
                                        >
                                            <option value="">Choose app</option>
                                            {advertisableApps?.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                        </Select>
                                    </Flex>
                                :null}

                            </Flex>
                        </Box>
                    </Flex>

                    {['APP', 'PHONE_CALL', 'WHATSAPP'].includes(adCreative?.adset?.destination_type) ? null :
                        <Flex direction={'column'} gap={2} borderRadius="md" bg={background} p={4} mb={4}>
                            <Text fontWeight={'bold'}>Tracking</Text>
                            <Flex direction="column" gap={1}>
                                <Flex alignItems={'center'}>
                                    <Text mb={2} fontWeight="bold">
                                        URL parameters{" "}
                                        {/* <Text as="span" fontWeight="bold" color={'gray.600'} mr={1}>.</Text> */}
                                        <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                                    </Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text >
                                                    Add parameters to the end of your website URL to track where your visitors are coming from.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                                    </Tooltip>
                                </Flex>
                                <Input
                                    name="creative.url_tags"
                                    placeholder="key1=value1&key2=value2"
                                    bg="#fff"
                                    size='sm'
                                    borderRadius="md"
                                    value={formik.values.creative.url_tags}
                                    onChange={formik.handleChange}
                                />
                                <Flex mt={2}>
                                    <Button onClick={() => {
                                        setUrlParamModalOpen(true)
                                    }}
                                        variant="link"
                                        color='brand'>
                                        <Text fontSize={'12px'} >{'Build a URL parameter'}</Text>
                                    </Button>
                                </Flex>
                            </Flex>
                        </Flex>
                    }

                    <Flex direction={'column'} gap={2} borderRadius="md" bg={background} p={4} mb={4}>
                        <Flex direction="column" gap={'10px'}>
                            <Text fontWeight="bold">CPA</Text>
                            <Input
                                name="cpa"
                                placeholder="Enter CPA"
                                bg="#fff"
                                size='sm'
                                borderRadius="md"
                                value={formik.values.cpa}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                            />
                            {formik.touched.cpa && formik.errors.cpa && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.cpa}</Text>
                            )}
                        </Flex>
                    </Flex>

                    <Flex>
                        <Button
                            size="sm"
                            colorScheme="brand"
                            isLoading={formik.isSubmitting}
                            type="submit"
                            mb={'20px'}
                        >
                            Save
                        </Button>
                    </Flex>
                </Flex>

                <Box pos={'relative'} flex={0.6} px={4} borderRadius={'md'} boxShadow="0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)">
                    <Flex py={2} gap={2}>
                        <Switch isChecked={isEnabled} colorScheme='brand' onChange={() => setIsEnabled(prevState => !prevState)} />
                        <Text fontWeight={'bold'} fontSize={'14px'}>Ad preview</Text>
                    </Flex>
                    <Divider />
                    {isEnabled && !isSaving &&
                        <AdPreviewCaro adCreative={adCreative} file={formik?.values} adsAccountData={adsAccountData} />
                    }
                    <Flex mt={2} alignItems={'center'} position={'absolute'} bottom={0}>
                        <Text fontSize={'12px'} mr={2}>Ad rendering and interaction may vary based on device, format and other factors.</Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                                <Box fontSize="12px" p="5px">
                                    <Text>
                                        Ads Manager previews are just examples and are not a substitute for reviewing Help Centre guidance and testing formats and devices to ensure that your ad delivers as intended.
                                    </Text>
                                </Box>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" boxSize={3} />
                        </Tooltip>
                    </Flex>
                </Box>
            </Flex>

            <Modal isOpen={isUrlModalOpen} onClose={() => setUrlModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <BuildUrlParamsUpdate link={formik.values.creative.object_story_spec.link_data} onClose={() => setUrlModalOpen(false)} onUrlChange={handleUrlChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>


            <Modal isOpen={isSingleUrlModalOpen} onClose={() => setSingleUrlModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <BuildUrlParamCarousel link={formik.values.creative.object_story_spec.link_data.child_attachments} onClose={() => setSingleUrlModalOpen(false)} onUrlChange={handleSingleUrlChange} selectedFileIndex={selectedFileIndex} />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={isUrlParamModalOpen} onClose={() => setUrlParamModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <UrlParametersUpdate link={formik.values.creative} onClose={() => setUrlParamModalOpen(false)} onUrlChange={handleUrlParamChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <MessageTemplateModal
                isOpen={isMessageModalOpen}
                onClose={onMessageModalClose}
                accountId={''}
                welcomeData={formik?.values?.creative?.object_story_spec?.link_data?.page_welcome_message}
                onSave={(updatedData) => {
                    formik.setFieldValue('creative.object_story_spec.link_data.page_welcome_message', updatedData);
                    onMessageModalClose();
                }}
            />

            {/* Drive Modal */}
            <DriveModal
                isDriveModalOpen={isDriveModalOpen}
                selectedAdAccounts={[{
                    id: adsAccountData?.id,
                    adsAccountId: adsAccountData?.account_id,
                    adsAccountName: adsAccountData?.name,
                    businessName: adsAccountData?.business?.name,
                    businessId: adsAccountData?.business?.id
                }]}
                onClose={() => setIsDriveModalOpen(false)}
                onSubmit={handleMediaSelect}
                uploadPurpose="update"
                mediaType={selectedMedia}
            />
        </form>
    );
};

export default CarouselAdUpdateForm;